import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import sortBy from 'lodash/sortBy';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useConfig } from '../../config';
import LoadingProgress from '../../layout/LoadingProgress';
import { isOrganizationAdminOrOwner, OrganizationFeature, OrganizationRole, UsagePlan } from '../../models';
import { SubscriptionStatus } from '../../models/subscriptionStatus';
import { useQuery } from '../../routes';
import {
  fromAuth,
  fromCurrentOrgUser,
  fromOrganizationRemovedUser,
  fromOrganizations,
  fromOrganizationUsers,
  fromSubscriptions,
  fromTestCloudSubscriptions,
  fromTestOpsProject,
  fromTestResultCount,
  fromUserInvitation,
  useAppDispatch,
} from '../../store';
import { getTestCloudFeature, getTestCloudFreeTrialDefault, getTestCloudFreeTrialExpiryDate } from '../subscription-management/utils';
import ProductSummary from './ProductSummary';
import QuickActions from './QuickActions';
import TestOpsMemberLicense from './TestOpsMemberLicense';
import UserSummary from './UserSummary';
import { DEFAULT_EXECUTIONS, UserLicense, convertSubscriptionSource } from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(4),
    height: '100%',
  },
  mainHome: {
    marginTop: theme.spacing(3),
    paddingRight: theme.spacing(4),
  },
  titleRole: {
    display: 'inline',
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'capitalize',
  },

  featuredDoc: {
    marginTop: theme.spacing(7.75),
  },
  docLink: {
    padding: theme.spacing(1, 0),
    cursor: 'pointer',
    fontWeight: 500,
    color: '#276EF1',
    fontSize: theme.spacing(2),
    width: 'fit-content',
  },
  loggedInfo: {
    color: '#516393',
    paddingBottom: theme.spacing(1),
  },
  featuredDocTitle: {
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #CFD7EC',
    marginBottom: theme.spacing(1),
  },
  quickActionContainer: {
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(4),
    },
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  productSummaryContainer: {
    marginTop: theme.spacing(3),
  },
}));

const PRODUCTS_LICENSES_LINK = 'https://docs.katalon.com/katalon-studio/docs/license.html';
const TO_OVERVIEW_LINK = 'https://docs.katalon.com/';
const ABOUT_PLATFORM_LINK = 'https://docs.katalon.com/docs';
const PLATFORM_PLANS_LINK = 'https://docs.katalon.com/docs/administer/katalon-platform-packages/katalon-platform-plans';
const ROLES_AND_PERMISSIONS_LINK = 'https://docs.katalon.com/docs/administer/administration-roles/administrative-roles-and-permissions';
const PLATFORM_GET_STARTED_LINK = 'https://docs.katalon.com/docs/get-started/get-started-with-the-katalon-platform';

export interface HomeProps {
  orgId: string,
}

const Home = (props: HomeProps) => {
  const { orgId } = props;
  const classes = useStyles();
  const intl = useIntl();
  const { get } = useQuery();
  const { config } = useConfig();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(fromAuth.selectUser);
  const subscribe = get('subscribe');
  const feature = OrganizationFeature.fromString(get('feature') || '');
  const [loading, setLoading] = useState(true);
  const currentOrgUser = useSelector(fromCurrentOrgUser.selectByOrganizationIdAndEmail(
    user?.email || '',
    Number(orgId),
  ));
  const testResultCount = useSelector(fromTestResultCount.selectLatestTestResultCount);
  const countActiveUsers = useSelector(fromOrganizationUsers.selectTotalOrgUsersCount);
  const countPendingInvitations = useSelector(fromUserInvitation.selectTotalUserInvitationsCount);
  const countRemovedUsers = useSelector(fromOrganizationRemovedUser
    .selectTotalOrganizationRemovedUsersCount);
  const currentOrganization = useSelector(
    fromOrganizations.selectOrganizationById(Number(orgId ?? 0)),
  );
  const subscriptions = useSelector(fromSubscriptions
    .selectSubscriptionByOrganizationId(Number(orgId) || 0));

  const countTestOpsProjects = useSelector(fromTestOpsProject
    .selectProjectCountByOrganizationId(Number(orgId)));

  const testOpsLicense: UserLicense = {
    feature: OrganizationFeature.TESTOPS,
    usagePlan: testResultCount?.usagePlan || UsagePlan.COMMUNITY,
    projects: countTestOpsProjects || 0,
    quota: testResultCount?.quota || DEFAULT_EXECUTIONS,
    source: testResultCount?.subscriptionSource,
  };

  const latestTestCloudSubscription = useSelector(
    fromTestCloudSubscriptions.selectLatestActiveTestCloudSubscriptionByOrganizationId(
      Number(orgId),
    ),
  );

  const testCloudSubscriptionCount = useSelector(
    fromTestCloudSubscriptions.selectCount,
  );
  const isTestCloudFreeTrialValid = new Date().valueOf() < getTestCloudFreeTrialExpiryDate(
    config,
    currentOrganization?.createdAt,
  ) && testCloudSubscriptionCount === 0;
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);
  const organization = useSelector(fromOrganizations.selectOrganizationById(Number(orgId)));

  // root/admin system have access similar to org owner
  const isAllowedAllAccess = isRootOrAdmin
    || (currentOrgUser && isOrganizationAdminOrOwner(currentOrgUser.role));

  const testCloudLicense: UserLicense = {
    feature: getTestCloudFeature(
      latestTestCloudSubscription,
      config,
      currentOrganization?.createdAt,
    ),
    quota: latestTestCloudSubscription === undefined
      ? (isTestCloudFreeTrialValid
        ? getTestCloudFreeTrialDefault().concurrentSessions
        : 0
      )
      : latestTestCloudSubscription.concurrentSessions,
  };

  const hasPlatformSubscription = subscriptions
    .find(sub => sub.feature === OrganizationFeature.TESTOPS_PLATFORM
      && sub.status === SubscriptionStatus.ACTIVE
      && new Date(sub.expiryDate).getTime() > Date.now()) !== undefined;

  const licenses: UserLicense[] = (
    sortBy(
      // Exclude all testop features like TESTOPS_BUSINESS, TESTOPS_ENTERPRISE, TESTOPS_PLATFORM
      subscriptions.filter(sub => !sub.feature.includes(OrganizationFeature.TESTOPS)
        && !sub.feature.includes(OrganizationFeature.VISUAL_TESTING_PRO))
        .map(it => ({
          feature: it.feature,
          quota: it.quantity,
          source: convertSubscriptionSource(it.source),
        })),
      license => OrganizationFeature.getFeatureHalfFullName(license.feature),
    )
  );
  if (!config?.onpremise) {
    licenses.push({ ...testCloudLicense });
  }

  useEffect(() => {
    const fetchInformation = async () => {
      if (user && config) {
        setLoading(true);
        if (currentOrgUser || isRootOrAdmin) {
          await Promise.all([
            dispatch(fromCurrentOrgUser
              .doGetOrgUserByUserIdAndOrgId({
                id: Number(user?.id),
                organizationId: Number(orgId),
              })),
            dispatch(fromTestResultCount.doGetLatestTestResultCount(+orgId)),
          ]);
        }
        if (currentOrgUser && !OrganizationRole.isMember(currentOrgUser?.role)) {
          await dispatch(fromSubscriptions
            .doGetActiveSubscriptionsByOrgId({ organizationId: +orgId }));
        }

        if (isAllowedAllAccess) {
          await Promise.all([
            dispatch(fromOrganizationUsers.doCountTotalOrgUsers({ organizationId: +orgId })),
            dispatch(fromUserInvitation.doCountTotalUserInvitations({ organizationId: +orgId })),
            dispatch(fromOrganizationRemovedUser
              .doGetAllOrganizationRemovedUser({ organizationId: +orgId })),
            config.onpremise ? undefined : dispatch(
              fromTestCloudSubscriptions.doGetTestCloudSubscriptionsByOrganizationId({
                organizationId: Number(orgId),
              }),
            ),
          ]);
        }

        if (subscribe === 'trial' && feature === OrganizationFeature.TESTCLOUD_MINUTE && !config.onpremise) {
          await dispatch(
            fromTestCloudSubscriptions.doGetTestCloudSubscriptionsByOrganizationId({
              organizationId: Number(orgId),
            }),
          ).then(unwrapResult).then(res => (res.total > 0
            ? enqueueSnackbar(
              <FormattedMessage id="home.trial_testcloud.error" values={{ plan: OrganizationFeature.getFeatureFullName(res.data[0].feature) }} />,
              {
                variant: 'error',
              },
            )
            : enqueueSnackbar(
              <FormattedMessage id="home.trial_testcloud.success" />,
              {
                variant: 'success',
              },
            )));
        }
        setLoading(false);
      }
    };
    fetchInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, orgId, currentOrgUser?.userId, currentOrgUser?.role, subscribe, feature, config]);

  if (!currentOrganization) return null;
  const legacyDocumentation = (
    <>
      <Link
        id="home.doc_link.products_and_licenses"
        underline="none"
        className={classes.docLink}
        href={PRODUCTS_LICENSES_LINK}
      >
        <FormattedMessage id="home.doc_link.products_and_licenses" />
      </Link>
      <Link
        id="home.doc_link.testops_overview"
        underline="none"
        className={classes.docLink}
        href={TO_OVERVIEW_LINK}
      >
        <FormattedMessage id="home.doc_link.testops_overview" />
      </Link>
    </>
  );

  const platformDocumentation = (
    <>
      <Link
        id="admin.home.link.about_platform"
        className={classes.docLink}
        href={ABOUT_PLATFORM_LINK}
        underline="none"
      >
        <FormattedMessage id="admin.home.link.about_platform" />
      </Link>
      <Link
        id="admin.home.link.roles_and_permissions"
        className={classes.docLink}
        href={ROLES_AND_PERMISSIONS_LINK}
        underline="none"
      >
        <FormattedMessage id="admin.home.link.roles_and_permissions" />
      </Link>
      <Link
        id="admin.home.link.platform_plans"
        className={classes.docLink}
        href={PLATFORM_PLANS_LINK}
        underline="none"
      >
        <FormattedMessage id="admin.home.link.platform_plans" />
      </Link>
      <Link
        id="admin.home.link.platform_get_started"
        className={classes.docLink}
        href={PLATFORM_GET_STARTED_LINK}
        underline="none"
      >
        <FormattedMessage id="admin.home.link.platform_get_started" />
      </Link>
    </>
  );

  return (
    <Grid container className={classes.root} justifyContent="space-between">
      {!loading && (currentOrgUser || isRootOrAdmin) && (
        <>
          <Grid item xs={12} sm={9} className={classes.mainHome}>
            <div>
              <Typography variant="h2">
                {intl.formatMessage(
                  { id: 'home.title' },
                  { orgName: organization?.name },
                )}
              </Typography>
              {currentOrgUser && (
                <Typography variant="subtitle2" className={classes.loggedInfo}>
                  {isOrganizationAdminOrOwner(currentOrgUser.role)
                    ? intl.formatMessage({ id: 'home.logged_in.an' })
                    : intl.formatMessage({ id: 'home.logged_in.a' })}
                  <Typography variant="subtitle2" className={classes.titleRole}>
                    {OrganizationRole.getRoleFullNameWithOrganization(currentOrgUser.role)}
                  </Typography>
                </Typography>
              )}
              {isAllowedAllAccess
                && (
                  <UserSummary
                    organization={currentOrganization}
                    countActiveUsers={countActiveUsers}
                    countPendingInvitations={countPendingInvitations}
                    countRemovedUsers={countRemovedUsers}
                  />
                )}
              <div className={classes.productSummaryContainer}>
                {isAllowedAllAccess && (
                  <Typography variant="h3">
                    {intl.formatMessage({ id: 'home.user_summary.my_products_title' })}
                  </Typography>
                )}
                {!OrganizationRole.isMember(currentOrgUser?.role) && (
                  <ProductSummary
                    licenses={licenses.filter(it => it.feature)}
                    testOpsLicense={testOpsLicense}
                  />
                )}
              </div>
            </div>
            {!loading
              && OrganizationRole.isMember(currentOrgUser?.role)
              && testResultCount?.quota !== 0 && (
                <TestOpsMemberLicense
                  testResultCount={testResultCount}
                  countTestOpsProjects={countTestOpsProjects}
                  orgId={orgId}
                />
            )}
            <div className={classes.featuredDoc}>
              <Typography variant="h3" className={classes.featuredDocTitle}>
                <FormattedMessage id="home.featured_doc" />
              </Typography>
              <div className={classes.linkContainer}>
                {hasPlatformSubscription ? (platformDocumentation) : (legacyDocumentation)}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.quickActionContainer}>
            <QuickActions
              role={currentOrgUser?.role}
              orgId={currentOrganization.id}
              accountId={currentOrganization.accountId}
            />
          </Grid>
        </>
      )}

      {loading && <LoadingProgress />}
    </Grid>
  );
};

export default Home;
